import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useGitStore } from '@/store/gitStore';
import { GitGraph } from './GitGraph';

/**
 * Визуализация Git репозитория - граф коммитов и три дерева
 * Интегрирована с gitStore для отображения актуального состояния
 *
 * Включает два режима отображения:
 * 1. Three Trees - диаграмма рабочих областей (Working, Staging, Repository)
 * 2. Git Graph - визуализация графа коммитов в стиле Figma
 */
export function GitVisualization() {
  const [activeArea, setActiveArea] = useState<'working' | 'staging' | 'repository'>('repository');
  const [viewMode, setViewMode] = useState<'trees' | 'graph'>('graph');
  const [animatingFiles, setAnimatingFiles] = useState<Set<string>>(new Set());
  const prevFilesRef = useRef<Set<string>>(new Set());

  const { repo, getHeadCommit } = useGitStore();

  const headCommit = getHeadCommit();

  // Анимация появления новых файлов
  useEffect(() => {
    const allFiles = new Set([
      ...repo.workingDir.map((f) => f.path),
      ...repo.staging.map((f) => f.path),
    ]);
    const newFiles = new Set<string>();

    allFiles.forEach((path) => {
      if (!prevFilesRef.current.has(path)) {
        newFiles.add(path);
      }
    });

    if (newFiles.size > 0) {
      setAnimatingFiles(newFiles);
      const timer = setTimeout(() => setAnimatingFiles(new Set()), 300);
      prevFilesRef.current = allFiles;
      return () => clearTimeout(timer);
    }

    prevFilesRef.current = allFiles;
  }, [repo.workingDir, repo.staging]);

  // Получаем метки веток для коммитов
  const getBranchLabels = (sha: string): string[] => {
    const labels: string[] = [];
    for (const [branchName, branchSha] of Object.entries(repo.branches)) {
      if (branchSha === sha) {
        labels.push(branchName);
      }
    }
    return labels;
  };

  return (
    <div className="flex h-full flex-col bg-slate-950 p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-300">Git Visualization</h4>
        <div className="flex gap-2">
          {/* Переключатель режима отображения */}
          <div className="flex rounded-lg bg-slate-800 p-0.5">
            <button
              onClick={() => setViewMode('graph')}
              className={cn(
                'rounded-md px-2 py-1 text-xs transition-colors',
                viewMode === 'graph'
                  ? 'bg-git-orange text-white'
                  : 'text-slate-400 hover:text-slate-200'
              )}
            >
              Graph
            </button>
            <button
              onClick={() => setViewMode('trees')}
              className={cn(
                'rounded-md px-2 py-1 text-xs transition-colors',
                viewMode === 'trees'
                  ? 'bg-git-orange text-white'
                  : 'text-slate-400 hover:text-slate-200'
              )}
            >
              Trees
            </button>
          </div>

          {/* Переключатель активной области (только для Trees) */}
          {viewMode === 'trees' && (
            <>
              <button
                onClick={() => setActiveArea('working')}
                className={cn(
                  'rounded px-2 py-1 text-xs transition-colors',
                  activeArea === 'working'
                    ? 'bg-area-working/20 text-area-working'
                    : 'text-slate-500 hover:text-slate-300'
                )}
              >
                Working
              </button>
              <button
                onClick={() => setActiveArea('staging')}
                className={cn(
                  'rounded px-2 py-1 text-xs transition-colors',
                  activeArea === 'staging'
                    ? 'bg-area-staging/20 text-area-staging'
                    : 'text-slate-500 hover:text-slate-300'
                )}
              >
                Staging
              </button>
              <button
                onClick={() => setActiveArea('repository')}
                className={cn(
                  'rounded px-2 py-1 text-xs transition-colors',
                  activeArea === 'repository'
                    ? 'bg-area-repository/20 text-area-repository'
                    : 'text-slate-500 hover:text-slate-300'
                )}
              >
                Repository
              </button>
            </>
          )}
        </div>
      </div>

      {/* Git Graph Mode */}
      {viewMode === 'graph' && (
        <div className="flex-1 min-h-0">
          <GitGraph className="h-full" showHeader={false} compact={false} />
        </div>
      )}

      {/* Three Trees Mode */}
      {viewMode === 'trees' && (
        <>
          {/* Three Trees Diagram */}
          <div className="mb-6 grid grid-cols-3 gap-4">
            {/* Working Directory */}
            <div
              className={cn(
                'rounded-lg border p-3 transition-all',
                activeArea === 'working'
                  ? 'border-area-working bg-area-working/10'
                  : 'border-slate-700 bg-slate-900/50'
              )}
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-area-working" />
                <span className="text-xs font-medium text-slate-300">Working Directory</span>
              </div>
              <div className="space-y-1">
                {repo.workingDir.length === 0 ? (
                  <div className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-400">
                    (empty)
                  </div>
                ) : (
                  repo.workingDir.map((file) => (
                    <div
                      key={file.path}
                      className={cn(
                        'flex items-center gap-2 rounded bg-slate-800 px-2 py-1 text-xs',
                        'transition-opacity duration-300',
                        animatingFiles.has(file.path) && 'animate-file-appear'
                      )}
                    >
                      <span
                        className={cn(
                          file.status === 'untracked' && 'text-red-400',
                          file.status === 'modified' && 'text-yellow-400',
                          file.status === 'deleted' && 'text-red-500',
                          file.status === 'renamed' && 'text-blue-400'
                        )}
                      >
                        {file.status === 'untracked' && '?'}
                        {file.status === 'modified' && 'M'}
                        {file.status === 'deleted' && 'D'}
                        {file.status === 'renamed' && 'R'}
                      </span>
                      <span className="text-slate-300 truncate">{file.path}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Staging Area */}
            <div
              className={cn(
                'rounded-lg border p-3 transition-all',
                activeArea === 'staging'
                  ? 'border-area-staging bg-area-staging/10'
                  : 'border-slate-700 bg-slate-900/50'
              )}
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-area-staging" />
                <span className="text-xs font-medium text-slate-300">Staging Area</span>
              </div>
              <div className="space-y-1">
                {repo.staging.length === 0 ? (
                  <div className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-400">
                    (empty)
                  </div>
                ) : (
                  repo.staging.map((file) => (
                    <div
                      key={file.path}
                      className={cn(
                        'flex items-center gap-2 rounded bg-slate-800 px-2 py-1 text-xs',
                        'transition-opacity duration-300',
                        animatingFiles.has(file.path) && 'animate-file-appear'
                      )}
                    >
                      <span
                        className={cn(
                          file.status === 'added' && 'text-green-400',
                          file.status === 'modified' && 'text-yellow-400',
                          file.status === 'deleted' && 'text-red-500',
                          file.status === 'renamed' && 'text-blue-400'
                        )}
                      >
                        {file.status === 'added' && 'A'}
                        {file.status === 'modified' && 'M'}
                        {file.status === 'deleted' && 'D'}
                        {file.status === 'renamed' && 'R'}
                      </span>
                      <span className="text-slate-300 truncate">{file.path}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Repository */}
            <div
              className={cn(
                'rounded-lg border p-3 transition-all',
                activeArea === 'repository'
                  ? 'border-area-repository bg-area-repository/10'
                  : 'border-slate-700 bg-slate-900/50'
              )}
            >
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-area-repository" />
                <span className="text-xs font-medium text-slate-300">Repository</span>
              </div>
              <div className="space-y-1">
                {!repo.initialized ? (
                  <div className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-400">
                    (not initialized)
                  </div>
                ) : Object.keys(repo.commits).length === 0 ? (
                  <div className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-400">
                    (no commits)
                  </div>
                ) : (
                  <div className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-400">
                    {Object.keys(repo.commits).length} commit(s)
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Commit History (упрощённый список) */}
          <div className="flex-1 overflow-auto">
            <h5 className="mb-3 text-xs font-medium text-slate-400">Commit History</h5>
            <div className="relative">
              {!repo.initialized ? (
                <div className="text-center text-sm text-slate-500">
                  Repository not initialized. Use "git init" to start.
                </div>
              ) : Object.keys(repo.commits).length === 0 ? (
                <div className="text-center text-sm text-slate-500">
                  No commits yet. Use "git commit" to create your first commit.
                </div>
              ) : (
                <div className="space-y-2 stagger-animation">
                  {Object.values(repo.commits)
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, 5)
                    .map((commit) => {
                      const branchLabels = getBranchLabels(commit.sha);
                      const isHead = headCommit?.sha === commit.sha;

                      return (
                        <div
                          key={commit.sha}
                          className={cn(
                            'flex items-center gap-3 rounded-lg p-3',
                            'transition-[background-color,transform,opacity] duration-300',
                            isHead
                              ? 'bg-slate-800 ring-1 ring-git-orange'
                              : 'bg-slate-900 hover:bg-slate-800'
                          )}
                        >
                          {/* Commit node */}
                          <div className="relative">
                            <div
                              className={cn(
                                'h-4 w-4 rounded-full transition-[background-color,transform] duration-200',
                                isHead ? 'bg-git-orange' : 'bg-slate-600',
                                isHead && 'animate-commit-pulse'
                              )}
                            />
                            {isHead && (
                              <div className="absolute -right-8 top-0 flex h-4 items-center animate-head-move">
                                <span className="text-[10px] font-bold text-git-orange">HEAD</span>
                              </div>
                            )}
                          </div>

                          {/* Commit info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-xs text-slate-400">
                                {commit.sha.slice(0, 7)}
                              </code>
                              {branchLabels.map((label) => (
                                <span
                                  key={label}
                                  className={cn(
                                    'branch-label text-[10px] px-1.5 py-0.5 rounded',
                                    'transition-[background-color,opacity] duration-200',
                                    label === 'main' || label === 'master'
                                      ? 'bg-git-orange/20 text-git-orange'
                                      : 'bg-blue-500/20 text-blue-400'
                                  )}
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                            <p className="mt-0.5 text-sm text-slate-300 truncate">
                              {commit.message}
                            </p>
                            <p className="text-[10px] text-slate-500">
                              {commit.author} • {new Date(commit.timestamp).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 border-t border-slate-800 pt-3">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-git-orange" />
          <span className="text-[10px] text-slate-500">main</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-area-working" />
          <span className="text-[10px] text-slate-500">Working</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-area-staging" />
          <span className="text-[10px] text-slate-500">Staging</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-area-repository" />
          <span className="text-[10px] text-slate-500">Repository</span>
        </div>
      </div>
    </div>
  );
}
